import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../components/Toast';
import { drawQrCard } from '../utils/qrCard';

const DEFAULTS = {
  content: '',
  heading: '',
  subtext: '',
  footer: '',
  foreground: '#111827',
  background: '#ffffff',
  accent: '#2563eb',
  size: 'medium',
  errorLevel: 'M',
  frame: true,
};

/** QR code for any text/URL with a heading, description and footer, downloadable as PNG. */
const QrCodeGeneratorPage = () => {
  const [options, setOptions] = useState(DEFAULTS);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);
  const { showError } = useToast();

  const set = (key) => (e) => {
    const value = e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setOptions((prev) => ({ ...prev, [key]: value }));
  };

  const render = useCallback(async () => {
    if (!canvasRef.current || !options.content.trim()) return;
    try {
      await drawQrCard(canvasRef.current, options);
      setError(null);
    } catch (err) {
      setError(err?.message?.includes('too big') ? 'Text is too long for a QR code. Shorten it or lower error correction.' : 'Could not generate the QR code.');
    }
  }, [options]);

  useEffect(() => {
    const timer = setTimeout(render, 150);
    return () => clearTimeout(timer);
  }, [render]);

  const download = () => {
    if (!canvasRef.current || !options.content.trim() || error) return;
    const name = (options.heading || 'qr-code').trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'qr-code';
    canvasRef.current.toBlob((blob) => {
      if (!blob) {
        showError('Could not create the image');
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  const hasContent = !!options.content.trim();

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-4 lg:p-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">QR Code Generator</h1>
        <p className="text-sm sm:text-base text-gray-600">
          Create a QR code for any link or text, add a heading and description, and download it as an image.
        </p>
      </div>
      <Card>
        <CardContent className="p-4 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="qr-content">QR content *</Label>
                  <Textarea
                    id="qr-content"
                    rows={3}
                    placeholder="https://example.com, Wi-Fi details, any text…"
                    value={options.content}
                    onChange={set('content')}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="qr-heading">Heading</Label>
                  <Input id="qr-heading" placeholder="e.g. Scan to register" value={options.heading} onChange={set('heading')} maxLength={80} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="qr-subtext">Description</Label>
                  <Textarea
                    id="qr-subtext"
                    rows={2}
                    placeholder="e.g. Open your camera and point it at the code"
                    value={options.subtext}
                    onChange={set('subtext')}
                    maxLength={200}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="qr-footer">Footer</Label>
                  <Input id="qr-footer" placeholder="e.g. Hall A · 10 Oct 2026" value={options.footer} onChange={set('footer')} maxLength={80} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    ['foreground', 'QR colour'],
                    ['background', 'Background'],
                    ['accent', 'Heading / frame'],
                  ].map(([key, label]) => (
                    <div key={key} className="space-y-1.5">
                      <Label htmlFor={`qr-${key}`}>{label}</Label>
                      <input
                        id={`qr-${key}`}
                        type="color"
                        value={options[key]}
                        onChange={set(key)}
                        className="h-10 w-full cursor-pointer rounded-md border border-gray-300 bg-white p-1"
                      />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="qr-size">Image size</Label>
                    <select id="qr-size" value={options.size} onChange={set('size')} className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm">
                      <option value="small">Small (600 px)</option>
                      <option value="medium">Medium (900 px)</option>
                      <option value="large">Large / print (1400 px)</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="qr-level">Error correction</Label>
                    <select id="qr-level" value={options.errorLevel} onChange={set('errorLevel')} className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm">
                      <option value="L">Low (most data)</option>
                      <option value="M">Medium</option>
                      <option value="Q">High</option>
                      <option value="H">Highest (most robust)</option>
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="h-4 w-4" checked={options.frame} onChange={set('frame')} />
                  Rounded frame around the card
                </label>
              </div>

              <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4">
                {hasContent ? (
                  <canvas ref={canvasRef} className="max-h-[70vh] w-auto max-w-full rounded-lg shadow-md" />
                ) : (
                  <p className="py-16 text-center text-sm text-gray-500">Enter the QR content to see a preview.</p>
                )}
                {error && <p className="text-sm text-red-600">{error}</p>}
                <Button onClick={download} disabled={!hasContent || !!error}>
                  <Download className="h-4 w-4 mr-2" />
                  Download PNG
                </Button>
              </div>
            </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QrCodeGeneratorPage;
